import { PlaceSummary } from "@/types/place"
import { isAnyOf } from "@reduxjs/toolkit"

import { SavedContentSync } from "@/lib/saved-content-sync"
import {
  collectionsApi,
  GetCollectionsArgs,
} from "@/lib/storage/api/collections-api"
import { placeApi } from "@/lib/storage/api/place-api"
import { savedInspirationsApi } from "@/lib/storage/api/saved-inspirations-api"
import { savedPlacesApi } from "@/lib/storage/api/saved-places-api"
import collectionSlice, {
  selectCurrentCollectionRef,
  selectCurrentPage,
} from "@/lib/storage/collection-slice"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { RootState } from "@/lib/storage/redux-store"
import {
  deleteSourceSummary,
  updatePlaceNote,
} from "@/lib/storage/saved-thunks"
import { selectTrip } from "@/lib/storage/trip-slice"
import { now } from "../date-utils"

const savedContentSyncMiddleware = createMtListenerMiddleware<RootState>(
  "saved-content-sync-middleware"
)

savedContentSyncMiddleware.startListening({
  matcher: savedPlacesApi.endpoints.addSavedPlace.matchFulfilled,
  effect: async (action, listenerApi) => {
    SavedContentSync.instance.broadcastContentChanged("places")

    const state = listenerApi.getState()
    const currentCollectionRef = selectCurrentCollectionRef(state)
    const { entity, collectionRef } = action.meta.arg.originalArgs
    const entityRef = entity.entity_ref

    listenerApi.dispatch(
      placeApi.util.invalidateTags([{ type: "Stats", id: entityRef }])
    )

    // Update getSavedPlacesByTrip cache
    const trip = selectTrip(state)
    const tripRef = trip?.ref
    const tripId = trip?.id
    if (tripRef && tripId !== 0) {
      listenerApi.dispatch(
        savedPlacesApi.util.updateQueryData(
          "getSavedPlacesByTrip",
          tripRef,
          (draft) => {
            if (!draft.some((sp) => sp.entity.entity_ref === entityRef)) {
              draft.unshift({
                entity: entity as PlaceSummary,
                updated_at: now(),
                collections: collectionRef ? [collectionRef] : undefined,
              })
            }
          }
        )
      )
    }

    // Update collection cache if this is the current collection
    if (currentCollectionRef && collectionRef === currentCollectionRef) {
      // Update the cache - serializeQueryArgs creates cache key from ref and viewToken
      // page doesn't matter for cache key lookup
      listenerApi.dispatch(
        collectionsApi.util.updateQueryData(
          "getCollection",
          { ref: currentCollectionRef, page: 1 },
          (draft) => {
            if (draft?.data) {
              const alreadyExists = draft.data.favorites.some(
                (fav) => fav.entity.entity_ref === entityRef
              )
              if (!alreadyExists) {
                draft.data.favorites.unshift({
                  entity: entity as PlaceSummary,
                  updated_at: now(),
                  collections: [collectionRef],
                })
                // Increment total count (not set to array length, since array is just current page)
                draft.data.favorites_count += 1
              }
            }
          }
        )
      )
    }

    // Update collections list with fresh summary from response
    const { savedCollectionSummary } = action.payload
    if (collectionRef && savedCollectionSummary) {
      const getCollectionsArgCombinations: GetCollectionsArgs[] = [
        { includeFavorites: true },
        { isPublic: true, includeFavorites: true },
        { isPublic: false, includeFavorites: true },
      ]
      getCollectionsArgCombinations.forEach((queryArgs) => {
        try {
          listenerApi.dispatch(
            collectionsApi.util.updateQueryData(
              "getCollections",
              queryArgs,
              (draft) => {
                const idx = draft.data.findIndex(
                  (c) => c.ref === savedCollectionSummary.ref
                )
                if (idx !== -1) {
                  draft.data[idx] = savedCollectionSummary
                }
              }
            )
          )
        } catch {
          // Cache entry doesn't exist for these args, ignore
        }
      })
    }
  },
})

savedContentSyncMiddleware.startListening({
  matcher: savedPlacesApi.endpoints.bulkAddSavedPlaces.matchFulfilled,
  effect: async (action, listenerApi) => {
    SavedContentSync.instance.broadcastContentChanged("places")

    const { entityRefs, collectionRef } = action.meta.arg.originalArgs

    // Invalidate stats for all added places
    for (const entityRef of entityRefs) {
      listenerApi.dispatch(
        placeApi.util.invalidateTags([{ type: "Stats", id: entityRef }])
      )
    }

    // Invalidate getSavedPlacesByTrip cache (we don't have full entity objects to optimistically update)
    listenerApi.dispatch(savedPlacesApi.util.invalidateTags(["SavedPlaces"]))

    listenerApi.dispatch(
      collectionsApi.util.invalidateTags([
        { type: "Collection", id: collectionRef },
      ])
    )

    // Update collections list with fresh summary from response
    const { savedCollectionSummary } = action.payload
    const getCollectionsArgCombinations: GetCollectionsArgs[] = [
      { includeFavorites: true },
      { isPublic: true, includeFavorites: true },
      { isPublic: false, includeFavorites: true },
    ]
    getCollectionsArgCombinations.forEach((queryArgs) => {
      try {
        listenerApi.dispatch(
          collectionsApi.util.updateQueryData(
            "getCollections",
            queryArgs,
            (draft) => {
              const idx = draft.data.findIndex(
                (c) => c.ref === savedCollectionSummary.ref
              )
              if (idx !== -1) {
                draft.data[idx] = savedCollectionSummary
              }
            }
          )
        )
      } catch {
        // Cache entry doesn't exist for these args, ignore
      }
    })
  },
})

savedContentSyncMiddleware.startListening({
  matcher: savedPlacesApi.endpoints.removeSavedPlace.matchFulfilled,
  effect: async (action, listenerApi) => {
    SavedContentSync.instance.broadcastContentChanged("places")

    const state = listenerApi.getState()
    const currentCollectionRef = selectCurrentCollectionRef(state)
    const args = action.meta.arg.originalArgs

    listenerApi.dispatch(
      placeApi.util.invalidateTags([{ type: "Stats", id: args.entityRef }])
    )

    // Update getSavedPlacesByTrip cache
    const trip = selectTrip(state)
    const tripRef = trip?.ref
    const tripId = trip?.id
    if (tripRef && tripId !== 0) {
      listenerApi.dispatch(
        savedPlacesApi.util.updateQueryData(
          "getSavedPlacesByTrip",
          tripRef,
          (draft) => {
            const index = draft.findIndex(
              (sp) => sp.entity.entity_ref === args.entityRef
            )
            if (index !== -1) {
              draft.splice(index, 1)
            }
          }
        )
      )
    }

    // Update collection cache if this is the current collection
    if (currentCollectionRef && args.collectionRef === currentCollectionRef) {
      const currentPage = selectCurrentPage(state)

      listenerApi.dispatch(
        collectionsApi.util.updateQueryData(
          "getCollection",
          { ref: currentCollectionRef, page: 1 },
          (draft) => {
            if (draft?.data) {
              const index = draft.data.favorites.findIndex(
                (fav) => fav.entity.entity_ref === args.entityRef
              )
              if (index !== -1) {
                draft.data.favorites.splice(index, 1)
                // Decrement total count (not set to array length, since array is just current page)
                draft.data.favorites_count = Math.max(
                  0,
                  draft.data.favorites_count - 1
                )
              }
            }
          }
        )
      )

      // If the current page is now empty and we're not on page 1,
      // navigate to the previous page
      const updatedState = listenerApi.getState()
      const result = collectionsApi.endpoints.getCollection.select({
        ref: currentCollectionRef,
        page: 1,
      })(updatedState)
      const favoritesCount = result.data?.data.favorites.length ?? 0

      if (favoritesCount === 0 && currentPage > 1) {
        listenerApi.dispatch(
          collectionSlice.actions.setCurrentPage(currentPage - 1)
        )
      }
    }
  },
})

savedContentSyncMiddleware.startListening({
  matcher: isAnyOf(
    savedInspirationsApi.endpoints.addSavedGuide.matchFulfilled,
    savedInspirationsApi.endpoints.removeSavedGuide.matchFulfilled
  ),
  effect: async () => {
    SavedContentSync.instance.broadcastContentChanged("guides")
  },
})

savedContentSyncMiddleware.startListening({
  actionCreator: updatePlaceNote.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const currentCollectionRef = selectCurrentCollectionRef(state)
    const { placeRef, collectionRef, updatedSavedPlace } = action.payload

    if (currentCollectionRef && collectionRef === currentCollectionRef) {
      listenerApi.dispatch(
        collectionsApi.util.updateQueryData(
          "getCollection",
          { ref: currentCollectionRef, page: 1 },
          (draft) => {
            if (draft?.data) {
              const favoriteIndex = draft.data.favorites.findIndex(
                (fav) => fav.entity.entity_ref === placeRef
              )
              if (favoriteIndex !== -1) {
                draft.data.favorites[favoriteIndex].notes =
                  updatedSavedPlace.notes
              }
            }
          }
        )
      )
    }
  },
})

savedContentSyncMiddleware.startListening({
  actionCreator: deleteSourceSummary.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const currentCollectionRef = selectCurrentCollectionRef(state)
    const { placeRef, collectionRef, updatedSavedPlace } = action.payload

    if (currentCollectionRef && collectionRef === currentCollectionRef) {
      listenerApi.dispatch(
        collectionsApi.util.updateQueryData(
          "getCollection",
          { ref: currentCollectionRef, page: 1 },
          (draft) => {
            if (draft?.data) {
              const favoriteIndex = draft.data.favorites.findIndex(
                (fav) => fav.entity.entity_ref === placeRef
              )
              if (favoriteIndex !== -1) {
                draft.data.favorites[favoriteIndex].notes =
                  updatedSavedPlace.notes
              }
            }
          }
        )
      )
    }
  },
})

export { savedContentSyncMiddleware }
